/**
 * ریل ناوبری.
 *
 * ⚠️ **ریل نسخه‌ی مشتری فقط دو مسیر دارد: «مشاوره» و «آرشیو تصمیم‌ها».**
 * هیچ مسیر مدیریتی در آن نیست و راهی برای بازگشت به حالت کارگاه وجود ندارد.
 *
 * ترتیب آیتم‌های استودیو همان فلو حاکم است: سازمان → دستیارها → پرونده‌ها →
 * تست → انتشار. این ترتیب آموزشی است، نه دلخواه: کاربر تازه‌وارد از بالا به
 * پایین می‌رود و هر گام پیش‌نیاز گام بعد است.
 *
 * ظاهر: ریل در هر دو حالتِ روشن و تاریک، تیره است. کنتراست ریل تیره با محتوای
 * روشن، سلسله‌مراتب صفحه را در یک نگاه می‌سازد و همان چیزی است که به رابط حس
 * ابزار حرفه‌ای می‌دهد.
 */

import {
  Archive,
  BrainCircuit,
  Building2,
  FlaskConical,
  FolderOpen,
  MessageSquareText,
  Rocket,
  Scale,
  Settings,
  Sparkles,
  Users,
  type LucideIcon,
} from 'lucide-react'
import { NavLink } from 'react-router'

import { cn } from '@/lib/cn'
import { useMeta } from '@/lib/meta-context'

interface NavItem {
  to: string
  label: string
  icon: LucideIcon
  hint?: string
}

const ASSISTANT_NAV: NavItem[] = [
  { to: '/consult', label: 'مشاوره', icon: MessageSquareText, hint: 'شروع یک برآورد تازه' },
  { to: '/archive', label: 'آرشیو تصمیم‌ها', icon: Archive, hint: 'مشاوره‌های گذشته' },
]

const STUDIO_NAV: NavItem[] = [
  { to: '/studio/organizations', label: 'سازمان‌ها', icon: Building2 },
  { to: '/studio/assistants', label: 'دستیارها', icon: Users },
  { to: '/studio/dossiers', label: 'پرونده‌های تصمیم', icon: FolderOpen },
  // بلافاصله پس از پرونده‌ها: اصل تأییدنشده وارد بازیابی نمی‌شود، پس این گام
  // بخشی از کار روی پرونده‌هاست نه یک ابزار جانبی.
  { to: '/studio/dossiers/principles', label: 'مرور اصول', icon: Scale },
  // پس از بارگذاری اسناد و پرونده‌ها می‌آید: ترتیب منو همان ترتیب کاری است.
  { to: '/studio/processing', label: 'پردازش دانش', icon: BrainCircuit },
  { to: '/studio/playground', label: 'تست دستیار', icon: FlaskConical },
  { to: '/studio/publish', label: 'انتشار', icon: Rocket },
]

const STUDIO_EXTRA_NAV: NavItem[] = [
  { to: '/archive', label: 'آرشیو', icon: Archive },
  { to: '/studio/settings', label: 'تنظیمات', icon: Settings },
]

/**
 * محتوای ریل، جدا از قاب.
 *
 * جدا است چون در دسکتاپ داخل `aside` ثابت می‌نشیند و در موبایل داخل کشوی
 * Dialog. یک منبع، دو قاب.
 */
export const SidebarNav = ({ onNavigate }: { onNavigate?: () => void }) => {
  const { meta } = useMeta()
  const items = meta.is_studio ? STUDIO_NAV : ASSISTANT_NAV

  return (
    <div className="flex h-full min-h-0 flex-col">
      <div className="flex items-center gap-3 px-5 py-5">
        {/* نشان برند: مربع گرادیانی با هاله. تنها نقطه‌ی رنگی پررنگ ریل. */}
        <span
          className="grid size-10 shrink-0 place-items-center rounded-md bg-linear-100 from-accent to-accent-two text-white shadow-glow ring-1 ring-inset ring-white/20"
          aria-hidden
        >
          <Sparkles className="size-5" />
        </span>
        <div className="min-w-0">
          <p className="truncate text-sm font-bold text-white">{meta.profile.app_title}</p>
          <p className="mt-0.5 truncate text-[11px] text-rail-ink-faint">
            {meta.is_studio ? 'محیط کارگاه' : meta.profile.organization_name}
          </p>
        </div>
      </div>

      <nav
        className="scrollbar-thin min-h-0 flex-1 overflow-y-auto px-3 pb-3"
        aria-label="ناوبری اصلی"
      >
        <RailGroup label={meta.is_studio ? 'جریان کار' : 'دستیار'}>
          {items.map((item) => (
            <RailLink key={item.to} item={item} onNavigate={onNavigate} />
          ))}
        </RailGroup>

        {meta.is_studio && (
          <RailGroup label="دیگر">
            {STUDIO_EXTRA_NAV.map((item) => (
              <RailLink key={item.to} item={item} onNavigate={onNavigate} />
            ))}
          </RailGroup>
        )}
      </nav>

      <div className="border-t border-rail-line px-5 py-4">
        <p className="text-[11px] leading-relaxed text-rail-ink-faint">
          خروجی این دستیار یک برآورد است، نه واقعیت قطعی.
        </p>
      </div>
    </div>
  )
}

const RailGroup = ({ label, children }: { label: string; children: React.ReactNode }) => (
  <div className="mt-3 first:mt-0">
    <p className="px-2.5 pb-2 text-[10px] font-semibold tracking-[0.14em] text-rail-ink-faint">
      {label}
    </p>
    <ul className="space-y-1">{children}</ul>
  </div>
)

const RailLink = ({ item, onNavigate }: { item: NavItem; onNavigate?: () => void }) => {
  const Icon = item.icon
  return (
    <li>
      <NavLink
        to={item.to}
        onClick={onNavigate}
        className={({ isActive }) =>
          cn(
            'group relative flex items-center gap-3 rounded-md px-2.5 py-2.5 text-sm transition-all duration-150',
            isActive
              ? 'bg-white/10 font-semibold text-white shadow-raised'
              : 'text-rail-ink hover:bg-white/6 hover:text-white',
          )
        }
      >
        {({ isActive }) => (
          <>
            {/* میله‌ی گرادیانی سمت شروع: نشانه‌ی فعال بودن، مستقل از رنگ متن. */}
            <span
              className={cn(
                'absolute inset-y-2 -start-3 w-1 rounded-full bg-linear-to-b from-accent to-accent-two transition-opacity',
                isActive ? 'opacity-100' : 'opacity-0',
              )}
              aria-hidden
            />
            {/* آیکون همیشه همراه برچسب متنی است. آیکون تنها، معنا را به حافظه‌ی
                بصری کاربر وابسته می‌کند. */}
            <Icon
              className={cn(
                'size-4.5 shrink-0 transition-colors',
                isActive ? 'text-accent' : 'text-rail-ink-faint group-hover:text-white',
              )}
              aria-hidden
            />
            <span className="min-w-0 truncate">{item.label}</span>
          </>
        )}
      </NavLink>
    </li>
  )
}

/** قاب دسکتاپ: ریل ثابت و تیره. */
export const Sidebar = () => (
  <aside
    className="sticky top-0 hidden h-dvh w-64 shrink-0 border-e border-rail-line bg-[linear-gradient(180deg,var(--t-rail-from),var(--t-rail-to))] lg:block"
  >
    <SidebarNav />
  </aside>
)
